import { useState, useRef, useEffect } from "react";

const WHATSAPP_NUMBER = "5512991220961";
const WHATSAPP_DIRECT_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=Ol%C3%A1%2C%20gostaria%20de%20entrar%20em%20contato.`;

const CASE_TYPES = [
  "Direito do Trabalho Empresarial",
  "Direito Previdenciário",
  "Direito Previdenciário Empresarial",
  "Direito do Servidor Público",
  "Direito do Trabalho",
  "Direito Civil",
  "Consultoria Jurídica",
  "Contencioso Estratégico",
  "Direito Empresarial",
  "Outro",
];

export default function ContatoSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [formState, setFormState] = useState({
    nome: "",
    email: "",
    tipo_caso: "",
    mensagem: "",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".reveal-contato");
            items.forEach((item, i) => {
              setTimeout(() => {
                item.classList.add("opacity-100", "translate-y-0");
                item.classList.remove("opacity-0", "translate-y-8");
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Impede o comportamento padrão do formulário (sem reload, sem fetch)
    e.preventDefault();

    // Validação do limite de caracteres da mensagem
    if (formState.mensagem.length > 500) {
      setError("A mensagem não pode ultrapassar 500 caracteres.");
      return;
    }

    // Monta a mensagem com a estrutura exata solicitada
    const mensagemTexto =
      `Nome Completo: ${formState.nome} | ` +
      `E-mail: ${formState.email} | ` +
      `Área do Caso: ${formState.tipo_caso} | ` +
      `Motivo do Contato: ${formState.mensagem}`;

    // Codifica a mensagem para uso em URL
    const mensagemCodificada = encodeURIComponent(mensagemTexto);

    // Monta o link do WhatsApp com a mensagem pré-preenchida
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${mensagemCodificada}`;

    // Abre o WhatsApp em nova aba (Web ou app mobile)
    window.open(whatsappUrl, "_blank");
  };

  return (
    <section id="contato" ref={sectionRef} className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="reveal-contato opacity-0 translate-y-8 transition-all duration-700 flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-10 bg-rose-primary" />
            <span className="text-rose-primary text-xs font-medium uppercase tracking-widest">Fale Conosco</span>
            <div className="h-px w-10 bg-rose-primary" />
          </div>
          <h2 className="reveal-contato opacity-0 translate-y-8 transition-all duration-700 font-heading text-4xl lg:text-5xl text-grafite font-semibold mb-4">
            Entre em contato
          </h2>
          <p className="reveal-contato opacity-0 translate-y-8 transition-all duration-700 text-grafite/60 text-base max-w-lg mx-auto">
            Preencha o formulário e envie sua mensagem diretamente pelo WhatsApp. Respondemos rapidamente.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Form */}
          <div className="reveal-contato opacity-0 translate-y-8 transition-all duration-700 lg:col-span-3">
            <div className="bg-white border border-gray-light rounded-2xl p-8">
              <form
                onSubmit={handleSubmit}
                id="contato-sm-advogados"
                className="flex flex-col gap-5"
              >
                {/* Nome Completo */}
                <div>
                  <label htmlFor="nome" className="block text-grafite text-xs font-medium mb-1.5 uppercase tracking-wide">
                    Nome Completo <span className="text-rose-primary">*</span>
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    required
                    value={formState.nome}
                    onChange={handleChange}
                    placeholder="Seu nome completo"
                    className="w-full border border-gray-light rounded-lg px-4 py-3 text-sm text-grafite placeholder-grafite/30 focus:outline-none focus:border-rose-primary transition-colors"
                  />
                </div>

                {/* E-mail */}
                <div>
                  <label htmlFor="email" className="block text-grafite text-xs font-medium mb-1.5 uppercase tracking-wide">
                    E-mail <span className="text-rose-primary">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formState.email}
                    onChange={handleChange}
                    placeholder="seu@email.com"
                    className="w-full border border-gray-light rounded-lg px-4 py-3 text-sm text-grafite placeholder-grafite/30 focus:outline-none focus:border-rose-primary transition-colors"
                  />
                </div>

                {/* Área do Caso */}
                <div>
                  <label htmlFor="tipo_caso" className="block text-grafite text-xs font-medium mb-1.5 uppercase tracking-wide">
                    Área do Caso <span className="text-rose-primary">*</span>
                  </label>
                  <select
                    id="tipo_caso"
                    name="tipo_caso"
                    required
                    value={formState.tipo_caso}
                    onChange={handleChange}
                    className="w-full border border-gray-light rounded-lg px-4 py-3 text-sm text-grafite focus:outline-none focus:border-rose-primary transition-colors cursor-pointer bg-white"
                  >
                    <option value="" disabled>Selecione a área do seu caso</option>
                    {CASE_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Mensagem */}
                <div>
                  <label htmlFor="mensagem" className="block text-grafite text-xs font-medium mb-1.5 uppercase tracking-wide">
                    Mensagem <span className="text-rose-primary">*</span>
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    required
                    rows={4}
                    maxLength={500}
                    value={formState.mensagem}
                    onChange={handleChange}
                    placeholder="Descreva brevemente sua situação jurídica..."
                    className="w-full border border-gray-light rounded-lg px-4 py-3 text-sm text-grafite placeholder-grafite/30 focus:outline-none focus:border-rose-primary transition-colors resize-none"
                  />
                  <p className="text-right text-xs text-grafite/40 mt-1">
                    {formState.mensagem.length}/500 caracteres
                  </p>
                </div>

                {error && (
                  <p className="text-rose-primary text-xs bg-rose-pale/50 rounded-lg px-4 py-3">{error}</p>
                )}

                {/* Botão — abre WhatsApp com mensagem pré-preenchida */}
                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 bg-rose-primary hover:bg-rose-dark text-white font-medium py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-base" />
                  </div>
                  Enviar pelo WhatsApp
                </button>

                <p className="text-center text-xs text-grafite/40">
                  Ao clicar em Enviar, você será direcionado ao WhatsApp com sua mensagem já preenchida.
                </p>
              </form>
            </div>
          </div>

          {/* Info + Map */}
          <div className="reveal-contato opacity-0 translate-y-8 transition-all duration-700 lg:col-span-2 flex flex-col gap-6">
            {/* Contact Info */}
            <div className="bg-grafite rounded-2xl p-7 text-white">
              <h3 className="font-heading text-lg text-white font-semibold mb-6">Informações de Contato</h3>
              <ul className="flex flex-col gap-5">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-rose-primary/20 rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-rose-light text-sm" />
                  </div>
                  <div>
                    <p className="text-white/90 text-sm font-medium mb-0.5">Endereço</p>
                    <p className="text-white/55 text-xs leading-relaxed">
                      Av. Cassiano Ricardo, 319 – Sala 1103<br />
                      Jardim de Aquário, São José dos Campos – SP
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-rose-primary/20 rounded-full flex-shrink-0">
                    <i className="ri-whatsapp-line text-rose-light text-sm" />
                  </div>
                  <div>
                    <p className="text-white/90 text-sm font-medium mb-0.5">WhatsApp</p>
                    <a
                      href={WHATSAPP_DIRECT_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/55 text-xs hover:text-rose-light transition-colors cursor-pointer"
                    >
                      +55 12 99122-0961
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-rose-primary/20 rounded-full flex-shrink-0">
                    <i className="ri-mail-line text-rose-light text-sm" />
                  </div>
                  <div>
                    <p className="text-white/90 text-sm font-medium mb-0.5">E-mail</p>
                    <a
                      href="mailto:samanthamarquesadvogados@gmail.com"
                      className="text-white/55 text-xs hover:text-rose-light transition-colors cursor-pointer break-all"
                    >
                      samanthamarquesadvogados@gmail.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-rose-primary/20 rounded-full flex-shrink-0">
                    <i className="ri-instagram-line text-rose-light text-sm" />
                  </div>
                  <div>
                    <p className="text-white/90 text-sm font-medium mb-0.5">Instagram</p>
                    <a
                      href="https://www.instagram.com/smadvogados.sjc"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-white/55 text-xs hover:text-rose-light transition-colors cursor-pointer"
                    >
                      @smadvogados.sjc
                    </a>
                  </div>
                </li>
              </ul>

              {/* WhatsApp CTA */}
              <a
                href={WHATSAPP_DIRECT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-rose-primary hover:bg-rose-dark text-white text-sm font-medium px-6 py-3 rounded-full mt-7 transition-all cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-base" />
                </div>
                Chamar no WhatsApp
              </a>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden border border-gray-light w-full h-56">
              <iframe
                title="Localização SM Advogados – Av. Cassiano Ricardo, São José dos Campos"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3667.3093!2d-45.8869!3d-23.1794!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94cc4a9a9a9a9a9b%3A0x0!2sAv.+Cassiano+Ricardo%2C+319+-+Sala+1103%2C+S%C3%A3o+Jos%C3%A9+dos+Campos+-+SP!5e0!3m2!1spt-BR!2sbr!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
